import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  CheckIcon,
  EyeIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { Notification } from '../../services/notificationService';

interface NotificationDropdownProps {
  notifications: Notification[];
  loading: boolean;
  onMarkAsRead: (notificationId: number) => void;
  onMarkAllAsRead: () => void;
  onClose: () => void;
  getPriorityColor: (priority: string) => string;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  loading,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose,
  getPriorityColor
}) => {
  const navigate = useNavigate();
  const unreadNotifications = notifications.filter(n => !n.is_read);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'patient_created':
        return '👤';
      case 'consultation_created':
        return '🩺';
      case 'invoice_created':
        return '💰';
      case 'appointment_scheduled':
        return '📅';
      case 'stock_low':
        return '📦';
      case 'stock_empty':
        return '⚠️';
      case 'system_alert':
        return '🔔';
      default:
        return '📄';
    }
  };

  const getNotificationTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'patient_created': 'Nouveau patient',
      'patient_updated': 'Patient modifié',
      'consultation_created': 'Nouvelle consultation',
      'consultation_completed': 'Consultation terminée',
      'appointment_scheduled': 'RDV programmé',
      'appointment_cancelled': 'RDV annulé',
      'invoice_created': 'Nouvelle facture',
      'invoice_paid': 'Facture payée',
      'stock_low': 'Stock faible',
      'stock_empty': 'Rupture de stock',
      'user_login': 'Connexion utilisateur',
      'system_alert': 'Alerte système',
    };
    return labels[type] || type;
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }

    // Naviguer vers l'URL d'action si elle existe
    if (notification.action_url) {
      navigate(notification.action_url);
      onClose();
    }
  };

  const formatTimeAgo = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: fr
    });
  };

  return (
    <div className="absolute right-0 mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-96 overflow-hidden">
      {/* En-tête */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Notifications
          </h3>
          {unreadNotifications.length > 0 && (
            <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
              {unreadNotifications.length}
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-1">
          {unreadNotifications.length > 0 && (
            <button
              onClick={onMarkAllAsRead}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
              title="Tout marquer comme lu"
            >
              <CheckIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Tout lire</span>
            </button>
          )}
          
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Fermer"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Liste des notifications */}
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Chargement...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-4 text-center">
            <div className="text-4xl mb-2">🔔</div>
            <p className="text-gray-500 text-sm">Aucune notification</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                  !notification.is_read ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start space-x-3">
                  {/* Icône de type */}
                  <div className="flex-shrink-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${getPriorityColor(notification.priority)} text-white`}>
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                  </div>
                  
                  {/* Contenu */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className={`text-sm font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                        {notification.title}
                      </p>
                      
                      {/* Indicateur non lu */}
                      {!notification.is_read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {notification.message}
                    </p>
                    
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <span className="bg-gray-100 px-2 py-1 rounded-full">
                          {getNotificationTypeLabel(notification.notification_type)}
                        </span>
                        
                        {notification.content_object_name && (
                          <span>• {notification.content_object_name}</span>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <ClockIcon className="h-3 w-3" />
                        <span>{formatTimeAgo(notification.created_at)}</span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center space-x-2 mt-2">
                      {!notification.is_read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onMarkAsRead(notification.id);
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
                        >
                          <EyeIcon className="h-3 w-3" />
                          <span>Marquer comme lu</span>
                        </button>
                      )}
                      
                      {notification.action_url && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(notification.action_url!);
                            onClose();
                          }}
                          className="text-xs text-green-600 hover:text-green-800 flex items-center space-x-1"
                        >
                          <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                          <span>Voir détails</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pied de page */}
      {notifications.length > 0 && (
        <div className="p-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => {
              navigate('/notifications');
              onClose();
            }}
            className="w-full text-sm text-blue-600 hover:text-blue-800 text-center"
          >
            Voir toutes les notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;